/**
 * The change calendar — Step 49.
 *
 * A month of planned windows, everyone's. That is the point: the queue asks
 * "what is mine?", this asks "what is happening on Tuesday, to anyone?" — and
 * it has to show changes owned by teams the viewer has nothing to do with, or
 * it cannot warn them about a clash.
 *
 * A month GRID rather than a list, because the question is spatial: two
 * changes on the same night is a thing you see instantly in a grid and have
 * to work out from a list.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { apiFetch } from '../lib/api';
import { qk } from '../lib/queryClient';
import { ticketRef } from '@itsm/shared';
import { ticketPath } from '../features/tickets/api';
import { cn } from '../lib/utils';
import { useAuth } from '../lib/AuthContext';

/** Step 143 */
interface Freeze {
  id: string;
  name: string;
  reason: string | null;
  startsAt: number;
  endsAt: number;
  ownerName: string | null;
  allowEmergency: boolean;
}
interface Window_ {
  id: string;
  number: number;
  type: string;
  subject: string;
  plannedStartAt: number | null;
  plannedEndAt: number | null;
  statusName: string | null;
  groupName: string | null;
  risk: string | null;
  kind: string | null;
}

const RISK_DOT: Record<string, string> = {
  low: 'bg-emerald-500',
  medium: 'bg-amber-500',
  high: 'bg-orange-500',
  critical: 'bg-destructive',
};

const MONTH = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });
const TIME = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });

/** Monday-first, because a change freeze runs Monday to Sunday in every
 *  change board this will meet. */
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function ChangeCalendarPage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const canManage = !!me?.permissions['org-settings']?.view;
  const [anchor, setAnchor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const gridStart = useMemo(() => {
    const d = new Date(anchor);
    // JS Sunday is 0; shift so Monday is the first column.
    const shift = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - shift);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [anchor]);
  const gridEnd = useMemo(() => {
    const d = new Date(gridStart);
    d.setDate(d.getDate() + 42);
    return d;
  }, [gridStart]);

  const q = useQuery({
    queryKey: qk.tickets.changeCalendar(gridStart.getTime(), gridEnd.getTime()),
    queryFn: () =>
      apiFetch<{ entries: Window_[] }>(
        `/api/tickets/calendar/changes?from=${gridStart.getTime()}&to=${gridEnd.getTime()}`,
      ),
  });

  // Step 143 — freeze windows over the same grid, shaded onto the days.
  const freezes = useQuery({
    queryKey: qk.freezeWindowsRange(gridStart.getTime(), gridEnd.getTime()),
    queryFn: () =>
      apiFetch<{ entries: Freeze[] }>(
        `/api/tickets/freeze-windows?from=${gridStart.getTime()}&to=${gridEnd.getTime()}`,
      ),
  });
  const days = useMemo(() => {
    const out: { date: Date; items: Window_[] }[] = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(gridStart);
      date.setDate(date.getDate() + i);
      const dayStart = date.getTime();
      const dayEnd = dayStart + 864e5;
      // A window belongs to every day it OVERLAPS, not just the one it starts
      // on — a three-day migration should appear on all three.
      const items = (q.data?.entries ?? []).filter(
        (e) => (e.plannedStartAt ?? 0) < dayEnd && (e.plannedEndAt ?? 0) > dayStart,
      );
      out.push({ date, items });
    }
    return out;
  }, [gridStart, q.data]);

  const today = new Date().setHours(0, 0, 0, 0);

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-lg font-semibold">Change calendar</h1>
          <p className="text-sm text-muted-foreground">
            Every planned window, whoever owns it. Amber days are freeze windows
            {canManage && (
              <>
                {' · '}
                <Link to="/admin/freeze-windows" className="underline hover:text-foreground">
                  manage them
                </Link>
              </>
            )}
            .
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))}
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-40 text-center text-sm font-medium">{MONTH.format(anchor)}</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))}
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {q.isPending && <Skeleton className="h-96 w-full" />}
      {q.isError && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <p className="text-sm font-medium text-destructive">Could not load the calendar</p>
            <Button size="sm" variant="outline" onClick={() => void q.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {q.data && (
        <div className="overflow-x-auto">
          <div className="grid min-w-[720px] grid-cols-7 gap-px rounded-md border bg-border">
            {WEEKDAYS.map((d) => (
              <div key={d} className="bg-muted/50 px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                {d}
              </div>
            ))}
            {days.map(({ date, items }) => {
              const outside = date.getMonth() !== anchor.getMonth();
              const isToday = date.setHours(0, 0, 0, 0) === today;
              const dayStart = date.getTime();
              const dayFreezes = (freezes.data?.entries ?? []).filter(
                (f) => f.startsAt < dayStart + 864e5 && f.endsAt > dayStart,
              );
              return (
                <div
                  key={date.toISOString()}
                  className={cn(
                    'min-h-24 bg-card p-1.5',
                    outside && 'bg-muted/30',
                    // Step 143 — a frozen day is tinted amber; the chip names the window.
                    dayFreezes.length > 0 && 'bg-amber-500/10',
                  )}
                >
                  <div
                    className={cn(
                      'mb-1 text-xs tabular-nums',
                      outside ? 'text-muted-foreground/50' : 'text-muted-foreground',
                      isToday && 'font-semibold text-brand',
                    )}
                  >
                    {date.getDate()}
                  </div>
                  {dayFreezes.map((f) => (
                    <p
                      key={f.id}
                      className="mb-1 truncate rounded bg-amber-500/20 px-1 py-0.5 text-[10px] font-medium text-amber-800 dark:text-amber-300"
                      title={`Freeze: ${f.name}${f.ownerName ? ` · ${f.ownerName}` : ''}${f.reason ? `\n${f.reason}` : ''}${f.allowEmergency ? '\nEmergency changes may proceed' : '\nBlocks every kind of change'}`}
                    >
                      Freeze · {f.name}
                    </p>
                  ))}
                  <div className="space-y-1">
                    {items.map((e) => (
                      <button
                        key={e.id}
                        onClick={() => navigate(ticketPath(e.type, e.id))}
                        title={`${ticketRef(e.type, e.number)} ${e.subject}\n${e.groupName ?? 'No group'} · ${e.statusName ?? ''}\n${e.plannedStartAt ? TIME.format(e.plannedStartAt) : ''}–${e.plannedEndAt ? TIME.format(e.plannedEndAt) : ''}`}
                        className="flex w-full items-center gap-1 rounded bg-accent/60 px-1 py-0.5 text-left text-[11px] hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {/* Risk is the one saturated colour here — the same
                            rule the queue's SLA pill follows. */}
                        <span
                          className={cn(
                            'h-1.5 w-1.5 shrink-0 rounded-full',
                            RISK_DOT[e.risk ?? ''] ?? 'bg-muted-foreground',
                          )}
                        />
                        <span className="min-w-0 truncate">{e.subject}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
